import type { UseQueryOptions } from "@pinia/colada";
import type { ObjectToCamel } from "ts-case-convert";
import type { MaybeRefOrGetter } from "vue";

import { createError } from "#app";
import { objectToCamel } from "ts-case-convert";
import { toValue } from "vue";

import type { AssetValue, ProductCms } from "@solvari/statamic";
import type { LocaleMorpheus } from "@solvari/translations";

import {
  isError,
  networkErrorToSentry,
  NotFoundError,
  returnNetworkError,
  throwError,
  toSearchParams,
} from "@solvari/utils";

import type { NarrowedCmsResponse } from "~/shared/libs/narrowedCmsResponse.ts";

import {
  kyCacheTags,
  setPageUncacheable,
} from "~/shared/composables/useCacheTags.ts";
import { cmsCollectionEntriesUrl } from "~/shared/composables/useCmsApi.ts";
import { useStatamicPreview } from "~/shared/composables/useStatamicPreview.ts";

// What `resolveLeadFormData` reads, plus `uri`: the only tag an ancestor rename purges a
// narrowed response through. Pinned in `tests/features/cmsNarrowedFields.spec.ts`.
const leadFormFields = ["id", "leadForm", "geoProductCategory", "uri"] as const;

type LeadFormPageResponse = NarrowedCmsResponse<
  ProductCms,
  typeof leadFormFields
>;

type ProductCmsData = ObjectToCamel<LeadFormPageResponse>["data"][number];

type LeadFormData = {
  formGroupId: number | null;
  formId: number | null;
  image: AssetValue | null;
  productName: string;
  title: string | null;
};

function resolveLeadFormData(
  entry: ProductCmsData,
  formSlug: string | null,
): LeadFormData | null {
  const { geoProductCategory, leadForm } = entry;

  if (formSlug) {
    const leadFormEntry = leadForm.leadForms.find(
      (form) => form.formSlug === formSlug,
    );

    if (!leadFormEntry) {
      return null;
    }

    return {
      formGroupId: Number(leadFormEntry.formGroupId) || null,
      formId: Number(leadFormEntry.formId) || null,
      image: leadFormEntry.image ?? leadForm.image ?? null,
      productName: geoProductCategory.name,
      title: leadFormEntry.title || null,
    };
  }

  return {
    formGroupId: geoProductCategory.formGroupId,
    formId: geoProductCategory.formId,
    image: leadForm.image ?? null,
    productName: geoProductCategory.name,
    title: leadForm.title || null,
  };
}

function useLeadFormPage(params: {
  formSlug: MaybeRefOrGetter<string | null>;
  geoProductCategoryUri: string;
  locale: LocaleMorpheus;
}) {
  const cmsUrl = cmsCollectionEntriesUrl("pages");

  const { previewToken, previewSite } = useStatamicPreview();

  return {
    key: () => [
      "cms",
      "lead-form",
      params.geoProductCategoryUri,
      toValue(params.formSlug),
      toValue(previewToken),
    ],
    query: throwError(async () => {
      const result = await returnNetworkError(async () =>
        objectToCamel(
          await kyCacheTags
            .get(cmsUrl, {
              searchParams: toSearchParams(
                {
                  fields: leadFormFields.join(","),
                  filter: {
                    blueprint: "product",
                    site: previewSite.value ?? params.locale,
                    uri: params.geoProductCategoryUri,
                  },
                  limit: 1,
                  token: previewToken.value,
                },
                { nestedKeys: true },
              ),
            })
            .json<LeadFormPageResponse>(),
        ),
      )();

      if (result instanceof NotFoundError) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }
      if (isError(result)) {
        networkErrorToSentry(result);
        setPageUncacheable();

        return result;
      }

      const entry = result.data[0];
      if (!entry) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }

      const leadFormData = resolveLeadFormData(entry, toValue(params.formSlug));
      if (!leadFormData) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }

      return leadFormData;
    }),
    staleTime: 5 * 60 * 1000,
  } satisfies UseQueryOptions;
}

export type { LeadFormData, ProductCmsData };

export { leadFormFields, resolveLeadFormData, useLeadFormPage };
